import { register, getHandler } from './index';
import { getInstanceDefinition } from '../cardLookup';
import {
  grantXp, grantPhysicalAttack, grantMagicAttack, grantGold, grantBuys, grantLight, drawCards, destroyFromHand, goldValueOf,
  isHeroLike, queueHandChoice, gainCurse, destroyCurseFromHand, discardFromHand, revealTopOfOwnDeck, applyInstanceOverride,
  estimateHeroAttackValue, removeMonsterFromHallToDiscard, moveHallCard, findCheapestBuyableSource, lookAtTopOfDeck,
  grantTemporaryStrength,
} from '../effectHelpers';
import { heroForEquippedWeapon, isWeaponEquipped } from '../../combat/attackMath';
import { performBuy } from '../../actions/villageActions';
import { buyOneCard } from '../buyOneCard';
import { performLevelUp } from '../../village/leveling';
import { compactAndRefill } from '../../dungeon/hall';
import { computeTotalLight } from '../../combat/lightDarkness';
import { computeRemainingGold } from '../../state/selectors';

function updatePlayer(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)) };
}

register('village-bandias-wisdom', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      for (const id of player.hand) {
        const def = getInstanceDefinition(nextState, id);
        if (def.strength === undefined || def.strength === null) continue;
        nextState = grantTemporaryStrength(nextState, playerId, id, 1);
      }
      nextState = grantXp(nextState, playerId, 3);
      // "Discard 2 XP at the end of your turn" - modeled as an immediate net
      // (+3 now, -2 at end-of-turn) collapsed to +1 net for simplicity, since
      // XP has no other end-of-turn hook in the engine yet.
      return grantXp(nextState, playerId, -2);
    },
  }],
});

register('village-battle-scarred-soldier', {
  village: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 1) }],
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const before = new Set(player.hand);
      const afterDraw = drawCards(state, playerId, 1);
      const drawnPlayer = afterDraw.players.find((p) => p.id === playerId);
      const newCardId = drawnPlayer.hand.find((id) => !before.has(id));
      if (!newCardId) return afterDraw;
      const def = getInstanceDefinition(afterDraw, newCardId);
      if (!isHeroLike(def)) return afterDraw;
      return grantPhysicalAttack(afterDraw, playerId, 2);
    },
  }],
});

register('village-bounty-hunter', {
  dungeon: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }],
  // "Spoils: Buy 1 village card."
  spoils: [buyOneCard({
    errorMessage: 'No village cards remain to buy.',
    includeBasics: false,
  })],
});

// Excludes Innkeeper's own instance from its own candidate list - same
// self-exclusion convention as druaDestroyAnyCandidates in heroes.js, so
// "destroy a villager" always presents a real choice rather than trivially
// self-targeting as its only always-available fallback.
function innkeeperCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => {
    if (id === instanceId) return false;
    const def = getInstanceDefinition(state, id);
    return isHeroLike(def) || def.traits.includes('Villager');
  });
}

register('village-innkeeper', {
  village: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const targetId = choices?.instanceId ?? innkeeperCandidates(state, { playerId, instanceId })[0];
      if (!targetId) throw new Error('No other villager or hero present to destroy.');
      return grantBuys(destroyFromHand(state, playerId, targetId), playerId, 1);
    },
    getCandidates: innkeeperCandidates,
  }],
});

register('village-king-caelans-writ', {
  village: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const heroGroups = Object.keys(state.village.heroStacks);
      const group = choices?.heroGroup ?? heroGroups.find((g) => state.village.heroStacks[g].length);
      if (!group) throw new Error('No hero stacks remain in the village.');
      const stack = state.village.heroStacks[group];
      if (!stack.length) throw new Error('That hero stack is empty.');
      const [topId, ...rest] = stack;
      let nextState = destroyFromHand(state, playerId, instanceId);
      nextState = {
        ...nextState,
        village: { ...nextState.village, heroStacks: { ...nextState.village.heroStacks, [group]: rest } },
      };
      nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, deck: [topId, ...p.deck] }));
      // "End your turn" - the card's own text overrides the default Village
      // step order (rulebook's First Rule: card text beats a general rule).
      return { ...nextState, meta: { ...nextState.meta, endTurnOverride: true } };
    },
  }],
});

register('village-mass-teleport', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      const drawn = drawCards(state, playerId, 3);
      return updatePlayer(drawn, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, dungeonAbilitiesLocked: true },
      }));
    },
  }],
});

register('village-moonstone', {
  dungeon: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 1) }],
});

register('village-rangers-wilderness-map', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      if (!state.dungeonDeck.length) return drawCards(state, playerId, 1);
      const dungeonDeck = [...state.dungeonDeck];
      const bottomId = dungeonDeck.pop();
      let nextState = { ...state, dungeonDeck };
      const bottomDef = getInstanceDefinition(nextState, bottomId);
      const isNonGuardianMonster = bottomDef.type === 'monster' && !bottomDef.traits.includes('Guardian');
      const targetRank = choices?.hallRankIndex;
      if (isNonGuardianMonster && targetRank !== undefined && targetRank >= 2 && nextState.dungeonHall[targetRank]) {
        const hall = [...nextState.dungeonHall];
        const swappedOut = hall[targetRank];
        hall[targetRank] = bottomId;
        nextState = { ...nextState, dungeonHall: hall, dungeonDeck: [...nextState.dungeonDeck, swappedOut] };
      } else {
        // Not switched (either not a legal target or no choice supplied) -
        // "if it is a non-guardian monster, you MAY switch it" - declining
        // leaves it revealed off the bottom; rather than invent a resting
        // place for it, it returns to the bottom of the deck.
        nextState = { ...nextState, dungeonDeck: [...nextState.dungeonDeck, bottomId] };
      }
      return drawCards(nextState, playerId, 1);
    },
  }],
});

register('village-royal-summons', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      const stack = state.village.basicStacks['basic-regular'] || [];
      const count = Math.min(3, stack.length);
      const taken = stack.slice(0, count);
      const remaining = stack.slice(count);
      let nextState = {
        ...state,
        village: { ...state.village, basicStacks: { ...state.village.basicStacks, 'basic-regular': remaining } },
      };
      return updatePlayer(nextState, playerId, (p) => ({ ...p, hand: [...p.hand, ...taken] }));
    },
  }],
  spoils: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const regulars = player.hand.filter((id) => getInstanceDefinition(state, id).id === 'basic-regular').slice(0, 3);
      let nextState = state;
      for (const id of regulars) nextState = destroyFromHand(nextState, playerId, id);
      return nextState;
    },
  }],
});

function smugglerCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => {
    if (id === instanceId) return false;
    const goldValue = goldValueOf(getInstanceDefinition(state, id));
    return goldValue !== undefined && goldValue >= 1;
  });
}

// Smuggler's React ability ("Destroy this card after a player loses a
// combat. Buy 1 card.") is special-cased directly in
// combat/resolveCombat.js (opens meta.smugglerReactWindowOpen on a loss)
// and actions/abilityActions.js's useSmugglerReact (the dispatched action
// that actually resolves it, same window-gated shape as Spoils abilities -
// see KNOWN_GAPS in effectRegistryCoverage.test.js) - only the Village
// ability is registered here.
register('village-smuggler', {
  village: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const targetId = choices?.instanceId ?? smugglerCandidates(state, { playerId, instanceId })[0];
      if (!targetId) throw new Error('No card with a gold value of 1 or higher present to destroy.');
      return grantGold(destroyFromHand(state, playerId, targetId), playerId, 4);
    },
    getCandidates: smugglerCandidates,
  }],
});

register('village-summon-storm', {
  village: [{
    resolve: (state, { playerId, instanceId }) => updatePlayer(state, playerId, (p) => ({
      ...p,
      hand: p.hand.filter((id) => id !== instanceId),
      deck: [instanceId, ...p.deck],
    })),
  }],
  dungeon: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 2) }],
});

// Deliberately def.type === 'hero', not isHeroLike: this levels the target
// within its own group's stack (performLevelUp's `group` branch), a
// mechanic Regular has no group to participate in - same category as
// Whetmage's documented gap (effectRegistry/heroes.js), not the generic
// "destroy/discard/count a hero" bug isHeroLike exists to fix. Regular
// leveling still works via the standard Village level-up flow (see
// village/leveling.js's getRegularLevelUpCandidates) - just not through
// this card's Spoils ability, which would need the same two-step "which
// level-1 hero" choice UI Whetmage L1 is still missing.
function veteranTrainerCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => {
    if (id === instanceId) return false;
    const def = getInstanceDefinition(state, id);
    return def.type === 'hero' && def.level < 3;
  });
}

// "Gain XP equal to the number of monsters you revealed" - the rulebook
// defines "revealed" broadly ("Total Attack Value: ...all cards you have
// revealed in a single turn" includes your own hand, shown to yourself and
// opponents alike when you Visit the Village or Enter the Dungeon). Monster
// cards essentially never end up in a hand under normal play (defeated
// monsters go straight to discard - see resolveCombat.js), so this counts
// monster-type cards currently in hand, which is almost always 0 but stays
// correct if some future card ever does put one there.
register('village-veteran-trainer', {
  village: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const monsterCount = player.hand.filter((id) => getInstanceDefinition(state, id).type === 'monster').length;
      return grantXp(state, playerId, monsterCount);
    },
  }],
  spoils: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = veteranTrainerCandidates(state, { playerId, instanceId });
      const targetId = choices?.heroInstanceId ?? candidates[0];
      if (!targetId) throw new Error('No hero available to level up.');
      const destroyed = destroyFromHand(state, playerId, instanceId);
      return performLevelUp(destroyed, playerId, targetId, { payXp: false, destination: 'discard' });
    },
    getCandidates: veteranTrainerCandidates,
  }],
});

// ==========================================================================
// Caverns of Bane
// ==========================================================================

// Animal Talisman: "Dungeon: Add one of the following to 1 hero: Magic
// Attack +2, or Light +2, or Strength +4." A choice between 3 effect
// TYPES, not just which card - but unlike Purple Worm's Battle-timing
// version of this same novelty (which needs the new queueEffectChoice
// pending-decision machinery), Dungeon-timing abilities already get real
// `choices` resolved before dispatch, so this is just a 3-way branch on
// `choices.effectType`, no new infrastructure needed. Magic Attack/Light
// are modeled as flat per-player pool additions (this engine's standing
// convention for "give X" effects that don't have a per-hero home, same
// simplification as Chirog/Culova/Margr/Sathosh's Trophy lines) rather
// than truly "to 1 hero" - only Strength is genuinely per-card, via
// grantTemporaryStrength on the chosen heroInstanceId (Thunderstone Shard's
// own precedent). Defaults to Magic Attack (the most broadly useful pick
// absent other info) when no choice is supplied.
register('village-animal-talisman', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const effectType = choices?.effectType ?? 'magic';
      if (effectType === 'magic') return grantMagicAttack(state, playerId, 2);
      if (effectType === 'light') return grantLight(state, playerId, 2);
      const player = state.players.find((p) => p.id === playerId);
      const candidates = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
      const targetId = choices?.heroInstanceId ?? candidates[0];
      if (!targetId) return state;
      return grantTemporaryStrength(state, playerId, targetId, 4);
    },
  }],
});

register('village-bloodhound', {
  dungeon: [
    {
      // "Physical Attack +2 if at least 1 hero is present."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const heroPresent = player.hand.some((id) => isHeroLike(getInstanceDefinition(state, id)));
        return heroPresent ? grantPhysicalAttack(state, playerId, 2) : state;
      },
    },
    {
      // "Physical Attack +1 if at least 1 other Bloodhound is present."
      resolve: (state, { playerId, instanceId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const otherPresent = player.hand.some((id) => id !== instanceId && getInstanceDefinition(state, id).id === 'village-bloodhound');
        return otherPresent ? grantPhysicalAttack(state, playerId, 1) : state;
      },
    },
  ],
});

register('village-castellan', {
  village: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 3) },
    {
      // "Destroy this card to draw 3 cards, and you may buy 1 additional cards this turn."
      resolve: (state, { playerId, instanceId }) => {
        const nextState = destroyFromHand(state, playerId, instanceId);
        return grantBuys(drawCards(nextState, playerId, 3), playerId, 1);
      },
    },
  ],
});

// Charm of Venery: "When this card is present, you may not visit the
// village." Special-cased directly on the VISIT_VILLAGE action itself (see
// actions/index.js's assertMayVisitVillage) - no ToR card gates an entire
// action based on hand presence, so there's no registry hook for it.

register('village-dwarven-ale', {
  dungeon: [{
    // "Add Physical Attack +1 and Strength +1 to each hero." Reuses
    // village-bandias-wisdom's own loop-and-override-Strength pattern above,
    // restricted to hero-like cards (Bandias Wisdom's own "to your heroes"
    // wording made the isHeroLike filter implicit; this card explicitly says
    // "to each hero"). Matt, bug report, with a precise repro: 3 Regulars +
    // Longspear read attack 4, using this ability should bring it to 7 (+1
    // per hero x 3), but it only went to 5. Root cause: grantPhysicalAttack
    // was called ONCE, outside the loop, so it always added a flat +1
    // regardless of hero count - Physical Attack is a pooled total (see
    // attackMath.js), but a "+1 to EACH hero" effect still needs to add
    // once per hero into that pool, same as the Strength half already
    // correctly does inside the loop.
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      for (const id of player.hand) {
        const def = getInstanceDefinition(nextState, id);
        if (!isHeroLike(def)) continue;
        nextState = grantTemporaryStrength(nextState, playerId, id, 1);
        nextState = grantPhysicalAttack(nextState, playerId, 1);
      }
      return nextState;
    },
  }],
  trophy: [{
    // "If a dwarf hero is present, 1 dwarf hero adds Physical Attack +4.
    // Destroy this card." Modeled as a flat +4 (which specific Dwarf
    // contributes it doesn't change the total) rather than prompting a
    // choice among equally-valued targets.
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const dwarfPresent = player.hand.some((id) => getInstanceDefinition(state, id).traits.includes('Dwarf'));
      if (!dwarfPresent) return state;
      return grantPhysicalAttack(destroyFromHand(state, playerId, instanceId), playerId, 4);
    },
  }],
});

register('village-force-blast', {
  dungeon: [
    { resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 2) },
    {
      // "Light +1 for each level of one wizard present." No tie-breaking
      // rule is printed for multiple Wizards - defaults to the highest
      // level present (maximizes the effect, same "pick the obviously best
      // target" default used elsewhere, e.g. bearer-belac's highest-Attack
      // pick), since a lower-level choice has no upside.
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const wizardLevels = player.hand
          .map((id) => getInstanceDefinition(state, id))
          .filter((def) => isHeroLike(def) && def.traits.includes('Wizard'))
          .map((def) => def.level || 0);
        if (!wizardLevels.length) return state;
        return grantLight(state, playerId, Math.max(...wizardLevels));
      },
    },
  ],
});

register('village-historian', {
  village: [
    {
      // "If a hero is present, gain 1 XP."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const heroPresent = player.hand.some((id) => isHeroLike(getInstanceDefinition(state, id)));
        return heroPresent ? grantXp(state, playerId, 1) : state;
      },
    },
    {
      // "Destroy this card. Draw 1 additional card after you refill your hand at the end of the turn."
      resolve: (state, { playerId, instanceId }) => {
        const nextState = destroyFromHand(state, playerId, instanceId);
        return { ...nextState, players: nextState.players.map((p) => (p.id === playerId ? { ...p, turnFlags: { ...p.turnFlags, bonusHandSizeAtEndOfTurn: p.turnFlags.bonusHandSizeAtEndOfTurn + 1 } } : p)) };
      },
    },
  ],
});

// Liveoak Staff: "Subtract the Strength of the equipped hero from this
// card's Physical Attack." combat/attackMath.js contributesTraits (mirrors
// village-snakehead-flail's own equipped-hero-lookup special case).

register('village-owl-eyes', {
  dungeon: [{
    // "Ignore Darkness penalty. Attack -2 for each point of Light." Reuses
    // the ignoreDarknessPenalty turnFlag built for Tuath Banisher's inverse
    // mechanic (see state/turnFlags.js).
    resolve: (state, { playerId }) => {
      const totalLight = computeTotalLight(state, playerId);
      return { ...state, players: state.players.map((p) => (p.id === playerId ? { ...p, turnFlags: { ...p.turnFlags, ignoreDarknessPenalty: true, bonusTotalAttack: p.turnFlags.bonusTotalAttack - 2 * totalLight } } : p)) };
    },
  }],
});

register('village-reckless-conjure', {
  dungeon: [{
    // "Draw 2 cards. Discard 4 cards."
    resolve: (state, { playerId }) => {
      let nextState = drawCards(state, playerId, 2);
      for (let i = 0; i < 4; i += 1) {
        const player = nextState.players.find((p) => p.id === playerId);
        nextState = queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds: player.hand, reason: 'Reckless Conjure: discard 4 cards.' });
      }
      return nextState;
    },
  }],
});

// Seedbomb: "React: When you level up a hero, destroy this card to put the
// lower-level hero in your discard pile instead of destroying it." -
// implemented in village/leveling.js's performLevelUp (useSeedbombInstanceId)
// and actions/villageActions.js's levelUpHero (choices.useSeedbombInstanceId,
// a real player choice), not through this registry - see those files' own
// comments. No register() entry needed since it isn't dispatched as a
// normal hand-card ability.

register('village-taproot-blade', {
  dungeon: [{
    // "Destroy 1 card to add Magic Attack +2 and Light +1." ("1 card," not
    // "this card" - a different card, per Thorn Caltrops's own explicit
    // "this card" wording below.)
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => id !== instanceId);
      let nextState = queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Taproot Blade: destroy 1 card.' });
      nextState = grantMagicAttack(nextState, playerId, 2);
      return grantLight(nextState, playerId, 1);
    },
  }],
});

function updatePlayerTurnFlags(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? { ...p, turnFlags: updateFn(p.turnFlags) } : p)) };
}

register('village-thorn-caltrops', {
  // "Cancel Raid effects." (alwaysActive, while equipped) - dungeon/hall.js
  // isRaidCancelledByThornCaltrops.
  dungeon: [{
    // "Destroy this card to cancel 1 Battle effect." Reuses Veilminder's own
    // cancelledBattleInstanceIds turnFlag and hallRankIndex-choice fallback
    // convention (see effectRegistry/heroes.js's veilminderCancel).
    resolve: (state, { playerId, instanceId, choices }) => {
      const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
      if (targetRank === -1 || !state.dungeonHall[targetRank]) throw new Error('No monster present to target.');
      const monsterInstanceId = state.dungeonHall[targetRank];
      let nextState = updatePlayerTurnFlags(state, playerId, (tf) => ({ ...tf, cancelledBattleInstanceIds: [...tf.cancelledBattleInstanceIds, monsterInstanceId] }));
      nextState = destroyFromHand(nextState, playerId, instanceId);
      return nextState;
    },
  }],
});

// ==========================================================================
// Root of Corruption
// ==========================================================================

register('village-blood-debt', {
  // "specialEffects: This card can only be destroyed by itself." Enforced by
  // effectHelpers.js's SELF_DESTROY_ONLY - every OTHER destroyFromHand call
  // site (Battle/Aftermath/Trophy/other Village or Dungeon abilities) leaves
  // selfDestroy at its default false and is silently blocked, so this is the
  // one and only place `{ selfDestroy: true }` is ever passed for this card.
  village: [{
    // "If you have 10 gold, destroy this card and end your turn."
    resolve: (state, { playerId, instanceId }) => {
      if (computeRemainingGold(state, playerId) < 10) return state;
      const nextState = destroyFromHand(state, playerId, instanceId, { selfDestroy: true });
      return { ...nextState, meta: { ...nextState.meta, endTurnOverride: true } };
    },
    // Matt reported this directly: "I keep getting stopped when I end my
    // village turn because the effect on my blood debt hasn't been used.
    // But I don't have the 10 gold required to use that ability so I
    // shouldn't be stopped." Without this, PlayerDock.vue's
    // isAbilityCurrentlyUsable (no isUsable/getCandidates hook registered
    // = defaults to true) always counted this as a real unused ability,
    // triggering the "still have unused abilities - end turn anyway?"
    // warning even when the 10-gold precondition plainly wasn't met.
    isUsable: (state, { playerId }) => computeRemainingGold(state, playerId) >= 10,
  }],
});

// Circle of Protection: "On your turn, place this card in front of you. It
// remains in play. Cancel the next Raid, Breach, or Aftermath effect that
// takes place on your turn and destroy this card." NOT YET IMPLEMENTED - a
// genuinely new persistent "in front of you" zone (distinct from hand/deck/
// discard) with an automatic cancel-and-self-destroy trigger spanning 3
// different timings (broader than Veilminder's manual, battle/aftermath-
// only, pre-declared cancellation). Flagged on docs/expansion-audit.md's
// review list.

register('village-cursed-dagger', {
  dungeon: [
    { resolve: (state, { playerId }) => grantPhysicalAttack(gainCurse(state, playerId), playerId, 1) },
    { resolve: (state, { playerId }) => grantPhysicalAttack(gainCurse(gainCurse(state, playerId), playerId), playerId, 2) },
  ],
});

// Debased Wizard: "Gain 1 curse. Use an ability on a card present that you
// have already used this turn." (village:0 and dungeon:0) - a "meta-
// ability" that re-triggers a DIFFERENT, already-used ability of the SAME
// timing (Village line reuses a Village ability, Dungeon line reuses a
// Dungeon ability - matching whichever phase Debased Wizard itself is
// being used in), bypassing the normal once-per-turn cap for that one
// re-trigger. The reused ability was already legitimately used once this
// turn (that's the precondition), so no bookkeeping is needed to "unblock"
// it afterward - it's already marked used, and stays that way. "Gain 1
// curse" always applies; the reuse half silently no-ops if nothing
// eligible exists (same "do what you can" convention as everywhere else),
// rather than failing the whole ability.
function debasedWizardReusableCandidates(state, playerId, timing) {
  const player = state.players.find((p) => p.id === playerId);
  const candidates = [];
  for (const instanceId of player.hand) {
    const def = getInstanceDefinition(state, instanceId);
    (def.abilityText[timing] || []).forEach((entry, abilityIndex) => {
      const key = `${instanceId}:${timing}:${abilityIndex}`;
      if (player.turnFlags.usedAbilityInstanceIds.includes(key) && getHandler(def.id, timing, abilityIndex)) {
        candidates.push({ instanceId, abilityIndex });
      }
    });
  }
  return candidates;
}

function debasedWizardReuse(timing) {
  return (state, { playerId, choices }) => {
    const nextState = gainCurse(state, playerId);
    const candidates = debasedWizardReusableCandidates(nextState, playerId, timing);
    if (!candidates.length) return nextState;
    const chosen = choices?.targetInstanceId
      ? candidates.find((c) => c.instanceId === choices.targetInstanceId
        && (choices.targetAbilityIndex === undefined || c.abilityIndex === choices.targetAbilityIndex))
      : candidates[0];
    if (!chosen) throw new Error('That ability is not eligible to be reused - it must already be used this turn.');
    const def = getInstanceDefinition(nextState, chosen.instanceId);
    const handler = getHandler(def.id, timing, chosen.abilityIndex);
    return handler(nextState, { playerId, instanceId: chosen.instanceId, choices: choices?.targetChoices || {} });
  };
}

register('village-debased-wizard', {
  village: [{ resolve: debasedWizardReuse('village') }],
  dungeon: [
    { resolve: debasedWizardReuse('dungeon') },
    {
      // "dungeon:1 - Select 1 Battle effect on a monster. Ignore that
      // Battle effect. Gain 1 curse." Reuses the same
      // cancelledBattleInstanceIds mechanism as Thorn Caltrops/Veilminder.
      resolve: (state, { playerId, choices }) => {
        const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
        if (targetRank === -1 || !state.dungeonHall[targetRank]) throw new Error('No monster present to target.');
        const monsterInstanceId = state.dungeonHall[targetRank];
        const nextState = updatePlayerTurnFlags(state, playerId, (tf) => ({ ...tf, cancelledBattleInstanceIds: [...tf.cancelledBattleInstanceIds, monsterInstanceId] }));
        return gainCurse(nextState, playerId);
      },
    },
  ],
});

// Elven Waybread: "Each of your heroes adds Strength +2." (dungeon:0,
// implemented below) is bundled with "Destroy this card. At the end of
// this turn, place 1 hero on top of your deck." (dungeon:1/village:0) - the
// card itself is destroyed immediately; the CHOSEN hero is deferred via
// turnFlags.pendingDeckPlacementInstanceIds, resolved in
// actions/turnActions.js's endTurn (see its own comment for why - the
// choice must be re-validated at actual end-of-turn, not locked in now).
function elvenWaybreadDeferPlacement(state, { playerId, instanceId, choices }) {
  const player = state.players.find((p) => p.id === playerId);
  const candidates = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
  const targetId = choices?.heroInstanceId ?? candidates[0];
  const nextState = destroyFromHand(state, playerId, instanceId);
  if (!targetId) return nextState;
  return updatePlayer(nextState, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, pendingDeckPlacementInstanceIds: [...p.turnFlags.pendingDeckPlacementInstanceIds, targetId] },
  }));
}

register('village-elven-waybread', {
  village: [{ resolve: elvenWaybreadDeferPlacement }],
  dungeon: [
    {
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        let nextState = state;
        for (const id of player.hand) {
          const def = getInstanceDefinition(nextState, id);
          if (!isHeroLike(def)) continue;
          nextState = grantTemporaryStrength(nextState, playerId, id, 2);
        }
        return nextState;
      },
    },
    { resolve: elvenWaybreadDeferPlacement },
  ],
});

// Greatsword: "Discard this card and its equipped hero to place a monster
// with Health less than or equal to the equipped hero's Attack Value on your
// discard pile (do not collect XP)." heroForEquippedWeapon identifies "its
// equipped hero"; estimateHeroAttackValue is computed BEFORE either card is
// discarded, so it still includes Greatsword's own Physical Attack +3 (Matt:
// "that's the hero's full modified attack value based on all effects that
// have been played, including equipping the sword"). Matt's second note:
// "this effect does not trigger a hall refill until the end of the turn, so
// it's possible to take more than one monster out of the hall, but you only
// get the options you had at the beginning of the turn" - removeMonsterFrom
// HallToDiscard leaves the rank empty rather than calling compactAndRefill
// (contrast handChoiceActions.js's defeatMonsterOutright, which refills
// immediately); turnActions.js's endTurn now runs compactAndRefill
// unconditionally at the start of every turn to catch up any rank(s) left
// behind this way. Rulebook glossary (TS9_Rulebook_04Jun.pdf p.8):
// "Discarding both weapon and hero are required. If you can't discard the
// hero, you can't use this effect."
function greatswordCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  const heroInstanceId = heroForEquippedWeapon(player, instanceId);
  if (!heroInstanceId) return [];
  const attackValue = estimateHeroAttackValue(state, player, heroInstanceId);
  return state.dungeonHall.filter((id) => id && getInstanceDefinition(state, id).health <= attackValue);
}

register('village-greatsword', {
  dungeon: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroInstanceId = heroForEquippedWeapon(player, instanceId);
      if (!heroInstanceId) throw new Error('Greatsword must be equipped to a hero to use this ability.');
      const candidates = greatswordCandidates(state, { playerId, instanceId });
      const monsterInstanceId = choices?.instanceId ?? candidates[0];
      if (!monsterInstanceId) throw new Error('No monster in the hall has Health low enough for this hero\'s Attack Value.');
      let nextState = discardFromHand(state, playerId, instanceId);
      nextState = discardFromHand(nextState, playerId, heroInstanceId);
      return removeMonsterFromHallToDiscard(nextState, playerId, monsterInstanceId);
    },
    getCandidates: greatswordCandidates,
  }],
});

register('village-hedge-witch', {
  village: [
    { resolve: (state, { playerId }) => (state.players.find((p) => p.id === playerId).xp >= 2 ? drawCards(grantXp(state, playerId, -2), playerId, 1) : state) },
    {
      // "repeatVillages:0 - Destroy 1 disease."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
        return diseaseId ? destroyCurseFromHand(state, playerId, diseaseId) : state;
      },
    },
  ],
  dungeon: [
    { resolve: (state, { playerId }) => (state.players.find((p) => p.id === playerId).xp >= 2 ? drawCards(grantXp(state, playerId, -2), playerId, 1) : state) },
    {
      // "repeatDungeons:0 - Destroy 1 disease."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
        return diseaseId ? destroyCurseFromHand(state, playerId, diseaseId) : state;
      },
    },
    {
      // "repeatDungeons:1 - Discard 2 XP to add Magic Attack +1."
      resolve: (state, { playerId }) => (state.players.find((p) => p.id === playerId).xp >= 2
        ? grantMagicAttack(grantXp(state, playerId, -2), playerId, 1) : state),
    },
  ],
});

register('village-hysterical-villager', {
  village: [
    {
      // "Destroy the top card of 1 hero stack."
      resolve: (state, { choices }) => {
        const groupKey = choices?.heroGroupKey ?? Object.keys(state.village.heroStacks).find((k) => state.village.heroStacks[k].length);
        if (!groupKey || !state.village.heroStacks[groupKey].length) return state;
        return { ...state, village: { ...state.village, heroStacks: { ...state.village.heroStacks, [groupKey]: state.village.heroStacks[groupKey].slice(1) } } };
      },
    },
    {
      // "Destroy this card to gain 3 gold."
      resolve: (state, { playerId, instanceId }) => grantGold(destroyFromHand(state, playerId, instanceId), playerId, 3),
    },
  ],
});

// Labrys: "Physical Attack +5" - printed trait line only, no other text.

register('village-liveoak-bow', {
  // "specialEffects: Subtract the selected monster's rank from this card's
  // Attack." combat/attackMath.js contributesTraits (needs hallRankIndex,
  // already threaded through for Caliginite's Darkness check).
});

register('village-maul', {
  // "Physical Attack -2" (printed, simple numeric - allowed to be negative,
  // same as any other alwaysActive line).
  dungeon: [{
    // "Add Physical Attack equal to the equipped hero's Strength."
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroInstanceId = Object.keys(player.equippedWeapons).find((h) => player.equippedWeapons[h].includes(instanceId));
      if (!heroInstanceId) return state;
      const heroDef = getInstanceDefinition(state, heroInstanceId);
      return grantPhysicalAttack(state, playerId, heroDef.strength || 0);
    },
  }],
});

// Mind Control: "Destroy this card. Place 1 monster from the hall in front
// of a player. Refill the hall. On that player's turn, that player must
// enter the dungeon and attack that monster if able (its rank and Darkness
// are 0). If the monster is not defeated, place it on the bottom of the
// dungeon deck." Two independent choices: which hall monster (the ordinary
// getCandidates convention handles this - PlayerDock.vue's resolveChoicesFor
// prompts via choices.instanceId same as any other card), and which PLAYER
// to assign it to (choices.targetPlayerId - explicitly NOT assumed to be
// "the other player": Matt asked for this directly, since the engine is
// meant to support up to 4 players eventually, not just 2. Defaults to the
// next player in turn order if omitted/invalid, same "reasonable default,
// real choice where the timing allows" convention as every other card here -
// PlayerDock.vue prompts a real picker over every player, reusing
// PlayerPickerPanel, same as the Drakeclan group's defeat-redirect choice).
// The assigned monster is pushed onto the target's inPlayInstanceIds - the
// shared "in front of you" zone also used by the familiar and Circle of
// Protection (state/createGameState.js) - and is distinguishable from
// those by its own card `type` alone (state/selectors.js's
// getAssignedMonsterInstanceId). The actual "must fight it" enforcement
// lives in state/selectors.js's hasAssignedMonsterObligation,
// actions/index.js (blocks Visit/Prepare/Rest), and dungeonActions.js's
// chooseMonster/combat/resolveCombat.js (hallRankIndex === -1 sentinel for
// "fight my assigned monster").
function mindControlCandidates(state) {
  return state.dungeonHall.filter(Boolean);
}

register('village-mind-control', {
  dungeon: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = mindControlCandidates(state);
      const monsterInstanceId = choices?.instanceId ?? candidates[0];
      if (!monsterInstanceId) throw new Error('No monster in the hall to assign.');
      const activeIndex = state.players.findIndex((p) => p.id === playerId);
      const defaultTargetId = state.players[(activeIndex + 1) % state.players.length].id;
      const targetPlayerId = (choices?.targetPlayerId && state.players.some((p) => p.id === choices.targetPlayerId))
        ? choices.targetPlayerId
        : defaultTargetId;

      let nextState = destroyFromHand(state, playerId, instanceId);
      const hallRankIndex = nextState.dungeonHall.indexOf(monsterInstanceId);
      const dungeonHall = [...nextState.dungeonHall];
      dungeonHall[hallRankIndex] = null;
      nextState = {
        ...nextState,
        dungeonHall,
        players: nextState.players.map((p) => (p.id === targetPlayerId
          ? { ...p, inPlayInstanceIds: [...p.inPlayInstanceIds, monsterInstanceId] }
          : p)),
      };
      return compactAndRefill(nextState, { playerId });
    },
    getCandidates: mindControlCandidates,
  }],
});

// Rage of the Disowned: "One hero adds Physical Attack +5 and cannot be
// discarded or destroyed by Dungeon abilities or Battle effects." /
// specialEffects "This card cannot be discarded or destroyed." (see
// effectHelpers.js's IMMUNE_TO_ALL_DISCARD_DESTROY) / Aftermath "Destroy
// that hero." The +5 lands in the normal pooled bonusPhysicalAttack
// (grantPhysicalAttack) rather than a per-hero-instance bonus - Total Attack
// Value math comes out identical either way, since nothing else here reads
// "this specific hero's own Attack Value" the way Belac/Rot Grub/Greatsword
// do; the chosen hero only matters for WHICH one becomes protected and gets
// destroyed by the Aftermath below, not for the attack total itself.
// turnFlags.protectedHeroInstanceIds is the same kind of transient
// cross-timing bridge as Dusk Hunter's meta.duskHunterRevealedInstanceId
// (monsters.js) - turnFlags instead of meta since it's naturally scoped to
// the acting player and already resets every turn on its own.
function rageOfTheDisownedCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
}

register('village-rage-of-the-disowned', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const heroInstanceId = choices?.heroInstanceId ?? rageOfTheDisownedCandidates(state, { playerId })[0];
      if (!heroInstanceId) return state;
      const nextState = grantPhysicalAttack(state, playerId, 5);
      return updatePlayer(nextState, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, protectedHeroInstanceIds: [...p.turnFlags.protectedHeroInstanceIds, heroInstanceId] },
      }));
    },
    getCandidates: rageOfTheDisownedCandidates,
  }],
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroInstanceId = player.turnFlags.protectedHeroInstanceIds[0];
      // No hero to destroy if this card's own Dungeon ability was never used
      // this turn (nothing queued), or if that hero already left the hand
      // some other way in the meantime.
      if (!heroInstanceId || !player.hand.includes(heroInstanceId)) return state;
      const cleared = updatePlayer(state, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, protectedHeroInstanceIds: p.turnFlags.protectedHeroInstanceIds.filter((id) => id !== heroInstanceId) },
      }));
      return destroyFromHand(cleared, playerId, heroInstanceId);
    },
  }],
});

register('village-stablehand', {
  village: [
    {
      // "Destroy 1 card in your discard pile." A Village-timing ability
      // (choices resolved before dispatch), so the target COULD be a real
      // player choice in principle, but there's no getCandidates here (and
      // deliberately isn't one yet) - PlayerDock.vue's picker only ever
      // highlights cards physically rendered in the hand row
      // (isChoiceCandidate/"click a highlighted card in your hand" - see its
      // own comment), and CollectionPanel.vue's discard viewer is
      // display-only with no click handling. Wiring a real discard-pile
      // picker needs its own UI, not just a getCandidates hook - tracked as
      // a known gap (same category as village-banish's hall-rearrange arm)
      // rather than silently shipping an unusable stuck picker.
      resolve: (state, { playerId, choices }) => {
        const player = state.players.find((p) => p.id === playerId);
        const targetId = choices?.instanceId ?? player.discard[0];
        if (!targetId || !player.discard.includes(targetId)) return state;
        return updatePlayer(state, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== targetId) }));
      },
    },
    {
      // "Destroy this card to place your deck into your discard pile."
      resolve: (state, { playerId, instanceId }) => {
        const nextState = destroyFromHand(state, playerId, instanceId);
        return updatePlayer(nextState, playerId, (p) => ({ ...p, deck: [], discard: [...p.discard, ...p.deck] }));
      },
    },
  ],
  dungeon: [{
    // Same discard-pile-picker gap as the Village line above - no
    // getCandidates until there's a real UI that can highlight/select a
    // discard-pile card, not just a hand card.
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = choices?.instanceId ?? player.discard[0];
      if (!targetId || !player.discard.includes(targetId)) return state;
      return updatePlayer(state, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== targetId) }));
    },
  }],
});

register('village-tincture-of-victims', {
  dungeon: [
    {
      // "dungeon:0 - Convert all Magic Attack to Physical Attack, or vice
      // versa. Discard 1 XP." Doesn't need to run any conversion math right
      // here - it just leaves turnFlags.attackConversionDirection behind for
      // combat/attackMath.js's computeBaseAttack to apply once the party's
      // Physical/Magic subtotals actually exist (see that file's own
      // comment). "Discard 1 XP" is mandatory, not optional - no "may" in
      // the printed text - so it always applies alongside the choice, not
      // gated behind it.
      resolve: (state, { playerId, choices }) => {
        const direction = choices?.direction === 'toMagic' ? 'toMagic' : 'toPhysical';
        const nextState = updatePlayer(state, playerId, (p) => ({
          ...p, turnFlags: { ...p.turnFlags, attackConversionDirection: direction },
        }));
        return grantXp(nextState, playerId, -1);
      },
    },
    {
      // "dungeon:1 - Add Physical Attack +2 and Strength +3 to one hero.
      // Destroy this card or gain 1 curse." A real 2-way choice, resolvable
      // via `choices` (Dungeon-timing) - defaults to destroying this card
      // (a spent Item) over gaining a curse (a lasting downside), the
      // cheaper of the two outcomes.
      //
      // Matt reported this directly: "I just use the tincture of victims,
      // dungeon ability, and it didn't let me choose which hero to give the
      // strength and attack bonuses to." Missing getCandidates - same class
      // of bug as Sage's own picker gap (see sage.test.js).
      resolve: (state, { playerId, instanceId, choices }) => {
        const player = state.players.find((p) => p.id === playerId);
        const heroId = choices?.heroInstanceId ?? player.hand.find((id) => isHeroLike(getInstanceDefinition(state, id)));
        let nextState = grantPhysicalAttack(state, playerId, 2);
        if (heroId) {
          nextState = grantTemporaryStrength(nextState, playerId, heroId, 3);
        }
        return choices?.outcome === 'curse' ? gainCurse(nextState, playerId) : destroyFromHand(nextState, playerId, instanceId);
      },
      getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand
        .filter((id) => isHeroLike(getInstanceDefinition(state, id))),
    },
  ],
});

// ==========================================================================
// Numenera
// ==========================================================================

function backpackReveal(state, { playerId, choices }) {
  const { state: s1, instanceId: r1 } = revealTopOfOwnDeck(state, playerId);
  const { state: s2, instanceId: r2 } = revealTopOfOwnDeck(s1, playerId);
  const revealed = [r1, r2].filter(Boolean);
  const qualifying = revealed.filter((id) => {
    const def = getInstanceDefinition(s2, id);
    return def.traits.includes('Item') || def.traits.includes('Weapon');
  });
  const targetId = choices?.instanceId && qualifying.includes(choices.instanceId) ? choices.instanceId : qualifying[0];
  if (!targetId) return s2;
  return updatePlayer(s2, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== targetId), hand: [...p.hand, targetId] }));
}
register('village-backpack', {
  village: [{ resolve: backpackReveal }],
  dungeon: [{ resolve: backpackReveal }],
});

register('village-bio-energy-cutter', {
  // "specialEffects: Magic Attack equal to the XP of the monster selected
  // to fight." - combat/attackMath.js contributesTraits (monsterXP param).
  spoils: [{
    // "Each other player discards 1 card."
    resolve: (state, { playerId }) => {
      let nextState = state;
      for (const p of state.players) {
        if (p.id === playerId) continue;
        const current = nextState.players.find((pp) => pp.id === p.id);
        if (current.hand.length) nextState = discardFromHand(nextState, p.id, current.hand[0]);
      }
      return nextState;
    },
  }],
});

function bioSalvagerDraw(state, { playerId }) {
  const before = new Set(state.players.find((p) => p.id === playerId).hand);
  let nextState = drawCards(state, playerId, 1);
  const drawnId = nextState.players.find((p) => p.id === playerId).hand.find((id) => !before.has(id));
  if (drawnId && getInstanceDefinition(nextState, drawnId).type === 'monster') nextState = drawCards(nextState, playerId, 1);
  return nextState;
}
register('village-bio-salvager', {
  village: [{ resolve: bioSalvagerDraw }],
  dungeon: [
    { resolve: bioSalvagerDraw },
    {
      // "Add Physical Attack +1 if a monster is present."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const hasMonster = player.hand.some((id) => getInstanceDefinition(state, id).type === 'monster');
        return hasMonster ? grantPhysicalAttack(state, playerId, 1) : state;
      },
    },
  ],
});

register('village-chemical-injector', {
  dungeon: [
    {
      // "Add Physical Attack +1 to each hero."
      resolve: (state, { playerId }) => {
        const heroCount = state.players.find((p) => p.id === playerId).hand.filter((id) => isHeroLike(getInstanceDefinition(state, id))).length;
        return heroCount ? grantPhysicalAttack(state, playerId, heroCount) : state;
      },
    },
    {
      // "Add Magic Attack +2 to one hero against Doomladen and Doomknights."
      // Two separate traitConditionalAttackBonuses entries (Doomladen,
      // Doomknight) since a monster could match either.
      resolve: (state, { playerId }) => updatePlayer(state, playerId, (p) => ({
        ...p,
        turnFlags: {
          ...p.turnFlags,
          traitConditionalAttackBonuses: [
            ...p.turnFlags.traitConditionalAttackBonuses,
            { trait: 'Doomladen', amount: 2, type: 'magic' },
            { trait: 'Doomknight', amount: 2, type: 'magic' },
          ],
        },
      })),
    },
  ],
});

register('village-dart-thrower', {
  // "specialEffects: Physical Attack +4 against rank 2 or higher." /
  // "Rangers of any Strength may equip this weapon. While equipped to a
  // ranger, this weapon's Attack can apply to rank 1." -
  // combat/attackMath.js contributesTraits + actions/dungeonActions.js's
  // equipWeapon strengthBypassed check.
});

// village-disruption-blade: "Physical Attack +3" only - no other text.

register('village-fearmaker', {
  // "Aftermath: If you did not defeat a monster, destroy this card. At the
  // end of this turn, take another turn." Reuses state.meta.extraTurnGranted
  // (the same mechanism the one other ToR "take another turn" card uses).
  aftermath: [{
    resolve: (state, { playerId, instanceId, won }) => {
      if (won) return state;
      const nextState = destroyFromHand(state, playerId, instanceId);
      return { ...nextState, meta: { ...nextState.meta, extraTurnGranted: true } };
    },
  }],
});

register('village-mental-scrambler', {
  dungeon: [
    { resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) },
    {
      // "Discard 4 cards from your deck. Draw 1 card." Removes directly
      // from the TOP OF DECK (not hand) - a bulk move into discard, no
      // player choice involved (unlike every hand-targeted "discard N").
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const discarded = player.deck.slice(0, 4);
        const nextState = updatePlayer(state, playerId, (p) => ({ ...p, deck: p.deck.slice(4), discard: [...p.discard, ...discarded] }));
        return drawCards(nextState, playerId, 1);
      },
    },
  ],
});

function nevajinCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
}

register('village-nevajin', {
  village: [
    {
      // "Destroy 1 hero. Gain gold equal to its Strength."
      resolve: (state, { playerId, choices }) => {
        const targetId = choices?.instanceId ?? nevajinCandidates(state, { playerId })[0];
        if (!targetId) return state;
        const strength = getInstanceDefinition(state, targetId).strength || 0;
        return grantGold(destroyFromHand(state, playerId, targetId), playerId, strength);
      },
      getCandidates: nevajinCandidates,
    },
    {
      // "Destroy this card to draw 1 card."
      resolve: (state, { playerId, instanceId }) => drawCards(destroyFromHand(state, playerId, instanceId), playerId, 1),
    },
  ],
});

// Paired Swords: "specialEffects: When you buy this card, you may place
// another copy of it from the village into your discard pile." - the only
// on-purchase trigger besides Filigree Amulet's own (already special-cased
// in actions/villageActions.js's performBuy) - handled the same way rather
// than a generic per-card purchase-hook framework for two cards.

register('village-partisan', {
  dungeon: [{
    // "Add Physical Attack +1 for each other equipped polearm present."
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const equippedIds = new Set(Object.values(player.equippedWeapons).flat());
      const count = player.hand.filter((id) => id !== instanceId && equippedIds.has(id) && getInstanceDefinition(state, id).traits.includes('Polearm')).length;
      return count ? grantPhysicalAttack(state, playerId, count) : state;
    },
  }],
});

function phaseDisruptorResolve(state, { playerId, instanceId }) {
  let nextState = destroyFromHand(state, playerId, instanceId);
  for (let i = 0; i < 2; i += 1) {
    const player = nextState.players.find((p) => p.id === playerId);
    const candidateInstanceIds = player.hand;
    nextState = queueHandChoice(nextState, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Phase Disruptor: destroy 2 other cards.' });
  }
  return drawCards(nextState, playerId, 1);
}
register('village-phase-disruptor', {
  village: [{ resolve: phaseDisruptorResolve }],
  dungeon: [{ resolve: phaseDisruptorResolve }],
});

// Punch Dagger: "specialEffects: Heroes may equip 2 of this card." NOT YET
// IMPLEMENTED. `equippedWeapons` is now a heroInstanceId -> weaponInstanceId[]
// map (see effectHelpers.js's getWeaponCapacity, added for Disowned
// Bloodrager's "equip two weapons," Caverns of Bane) - the general "more
// than 1 weapon" plumbing exists, but this card's rule is narrower and
// per-WEAPON rather than per-hero (ANY hero may hold 2 Punch Daggers
// specifically, not just Disowned Bloodrager holding any 2 weapons), so
// getWeaponCapacity's allowlist doesn't cover it as-is. See
// docs/expansion-audit.md.

register('village-ray-emitter', {
  dungeon: [
    {
      // "Add Light +1 if a cleric is present." (source text says "List",
      // an evident typo for "Light" - the only sensible reading among the
      // engine's own tracked stats.)
      resolve: (state, { playerId }) => {
        const hasCleric = state.players.find((p) => p.id === playerId).hand.some((id) => getInstanceDefinition(state, id).traits.includes('Cleric'));
        return hasCleric ? grantLight(state, playerId, 1) : state;
      },
    },
    {
      // "Add Strength +2 to each hero."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        let nextState = state;
        for (const id of player.hand) {
          const def = getInstanceDefinition(nextState, id);
          if (!isHeroLike(def)) continue;
          nextState = grantTemporaryStrength(nextState, playerId, id, 2);
        }
        return nextState;
      },
    },
  ],
  spoils: [{
    // "Gain XP equal to the rank of the monster defeated." (source text
    // says "he monster," an evident typo for "the monster.")
    resolve: (state, { playerId }) => {
      const hallRankIndex = state.meta.lastCombatResult?.hallRankIndex;
      if (hallRankIndex === undefined || hallRankIndex === null) return state;
      return grantXp(state, playerId, hallRankIndex + 1);
    },
  }],
});

register('village-retaliation-nodule', {
  dungeon: [
    { resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) },
    {
      // "Add Magic Attack +1 for each hero class present." Distinct classes
      // present, same convention as bearer-goron-singlmind's own
      // "for each class present" (Caverns of Bane).
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const classCount = ['Fighter', 'Wizard', 'Cleric', 'Thief', 'Ranger'].filter((cls) => player.hand.some((id) => getInstanceDefinition(state, id).traits.includes(cls))).length;
        return classCount ? grantMagicAttack(state, playerId, classCount) : state;
      },
    },
  ],
});

// "React: When one or more heroes would be destroyed by a monster's effect,
// destroy this card instead." No react: [...] entry here - it's a generic
// substitution offered wherever a hero is actually about to be destroyed,
// not a per-card effect. See effectHelpers.js's queueShieldBearerOffer/
// findEligibleShieldBearer and actions/handChoiceActions.js's
// destroyHeroOrOfferShieldBearer/resolveShieldBearerOffer.
register('village-shield-bearer', {
  dungeon: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }],
});

register('village-shock-nodule', {
  dungeon: [{
    // "Draw until you have 8 cards."
    resolve: (state, { playerId }) => {
      const handSize = state.players.find((p) => p.id === playerId).hand.length;
      const needed = Math.max(0, 8 - handSize);
      return needed ? drawCards(state, playerId, needed) : state;
    },
  }],
});

register('village-unstable-crystals', {
  dungeon: [{
    // "Add Magic Attack +3 to one hero. Place a copy of this card from the
    // village into your discard pile."
    resolve: (state, { playerId }) => {
      let nextState = grantMagicAttack(state, playerId, 3);
      const stack = nextState.village.villageStacks['village-unstable-crystals'];
      if (stack && stack.length) {
        nextState = performBuy(nextState, playerId, { type: 'village', key: 'village-unstable-crystals' }, { requireGoldCheck: false });
      }
      return nextState;
    },
  }],
  // "specialEffects: If 3 or more copies of this card are present, destroy
  // all copies of this card and 1 random card. End your turn." - see
  // actions/abilityActions.js's resolveUnstableCrystalsCascade (checked at
  // hand-reveal time, same as Trophy effects/Circle of Protection's own
  // placement).
});

register('village-village-headman', {
  dungeon: [{
    // "Add Physical Attack +1 for each villager present (including this one)."
    resolve: (state, { playerId }) => {
      const count = state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Villager')).length;
      return count ? grantPhysicalAttack(state, playerId, count) : state;
    },
  }],
});

register('village-village-jack', {
  village: [{
    // "Destroy 1 card to gain 2 gold or 1 XP. (This card may destroy
    // itself.)" A real 2-way choice, resolvable via `choices` (Village
    // timing) - same convention as Silvertongue L2/Tincture of Victims.
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = choices?.instanceId ?? player.hand[0];
      if (!targetId) return state;
      const nextState = destroyFromHand(state, playerId, targetId);
      return choices?.rewardType === 'xp' ? grantXp(nextState, playerId, 1) : grantGold(nextState, playerId, 2);
    },
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand,
  }],
});

function warderPeek(state, { playerId, choices }) {
  // "Look at the top card of any deck. If it is not the dungeon deck, you
  // may put the card on the bottom of the deck." Defaults to the acting
  // player's own deck - the engine has no "reveal to only 1 player" concept
  // for peeking at an OPPONENT's deck, and "you may" defaults to accepting
  // when there's a legal target, same convention as other "you may"
  // abilities elsewhere.
  const targetPlayerId = choices?.targetPlayerId ?? playerId;
  const target = state.players.find((p) => p.id === targetPlayerId);
  if (!target || !target.deck.length) return state;
  const [topId, ...rest] = target.deck;
  return updatePlayer(state, targetPlayerId, (p) => ({ ...p, deck: [...rest, topId] }));
}
register('village-warder', {
  village: [{ resolve: warderPeek }],
  dungeon: [{ resolve: warderPeek }],
});

// ==========================================================================
// Starter set
// ==========================================================================

// village-bardiche: "Add Physical Attack +1 while attacking a monster in
// Rank 1." - combat/attackMath.js contributesTraits (hallRankIndex).
// village-champion-sword / village-runed-hammer: printed Attack line only.

register('village-curative-draught', {
  village: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
      return diseaseId ? destroyCurseFromHand(state, playerId, diseaseId) : state;
    },
  }],
  dungeon: [{
    // "Destroy 1 disease, or destroy this card to add Physical Attack +1
    // to one hero." A real 2-way choice, resolvable via `choices`
    // (Dungeon timing) - defaults to curing a disease when one's present
    // (keeps this card around for its Village-timing cure too), otherwise
    // falls back to destroying itself for the Attack bonus.
    resolve: (state, { playerId, instanceId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
      const wantsSelfDestroy = choices?.outcome === 'attack' || !diseaseId;
      if (wantsSelfDestroy) return grantPhysicalAttack(destroyFromHand(state, playerId, instanceId), playerId, 1);
      return destroyCurseFromHand(state, playerId, diseaseId);
    },
  }],
});

// Insight: "Rearrange the dungeon hall (you may not move Guardians). Draw 1
// card." Reuses hero-bhoidwood-l3/Banish's own full-permutation primitive
// (choices.newOrder, an array of instanceId|null covering every rank) -
// unlike THOSE two cards' own printed text (plain "rearrange the hall," no
// restriction), Insight adds a Guardian carve-out, validated here rather
// than in the shared primitive itself (which must stay unrestricted for
// Bhoidwood/Banish). No-ops the rearrange half without a UI-supplied order
// (same "don't guess an arrangement on the player's behalf" convention as
// Bhoidwood-l3) - "Draw 1 card" still applies either way, since it's not
// conditioned on the rearrange actually happening.
function isGuardianAt(state, hall, rankIndex) {
  const instanceId = hall[rankIndex];
  return Boolean(instanceId) && getInstanceDefinition(state, instanceId).traits.includes('Guardian');
}
register('village-insight', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      let nextState = state;
      if (choices?.newOrder) {
        if (choices.newOrder.length !== state.dungeonHall.length) throw new Error('New hall order must cover every rank.');
        for (let rank = 0; rank < state.dungeonHall.length; rank += 1) {
          if (isGuardianAt(state, state.dungeonHall, rank) && state.dungeonHall[rank] !== choices.newOrder[rank]) {
            throw new Error('Insight: Guardians cannot be moved.');
          }
        }
        nextState = { ...state, dungeonHall: [...choices.newOrder] };
      }
      return drawCards(nextState, playerId, 1);
    },
  }],
});

register('village-novice-mage', {
  village: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 1) }],
  dungeon: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) }],
});

function opportunistCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => ['basic-regular', 'basic-torch', 'basic-longspear'].includes(getInstanceDefinition(state, id).id));
}

register('village-opportunist', {
  village: [
    {
      // "Destroy 1 Regular, Torch, or Longspear to gain 2 gold. Discard
      // this card." (source data used to say "Lonspear," a transcription
      // typo confirmed against the card scan and fixed at the source during
      // the Starter Set audit - the underlying basic-longspear id below was
      // always correct.)
      resolve: (state, { playerId, instanceId, choices }) => {
        const targetId = choices?.instanceId ?? opportunistCandidates(state, { playerId })[0];
        if (!targetId) return state;
        let nextState = grantGold(destroyFromHand(state, playerId, targetId), playerId, 2);
        return discardFromHand(nextState, playerId, instanceId);
      },
      getCandidates: opportunistCandidates,
    },
    {
      resolve: (state, { playerId, instanceId }) => grantXp(destroyFromHand(state, playerId, instanceId), playerId, 1),
    },
  ],
});

// village-radiant-orb: printed Light stat only, no ability text.

register('village-rolling-sparks', {
  dungeon: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 2) }],
});

register('village-summon-aid', {
  dungeon: [{
    // "Reveal the top 2 cards of your deck. Place any revealed heroes into
    // your hand. Discard the rest." Unlike Backpack's own 2-card reveal
    // (which places at most 1 qualifying card), this keeps EVERY qualifying
    // card revealed, not just one.
    resolve: (state, { playerId }) => {
      const { state: s1, instanceId: r1 } = revealTopOfOwnDeck(state, playerId);
      const { state: s2, instanceId: r2 } = revealTopOfOwnDeck(s1, playerId);
      const heroIds = [r1, r2].filter((id) => id && isHeroLike(getInstanceDefinition(s2, id)));
      if (!heroIds.length) return s2;
      return updatePlayer(s2, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => !heroIds.includes(id)), hand: [...p.hand, ...heroIds] }));
    },
  }],
});

// ==========================================================================
// Into the Abyss
// ==========================================================================

register('village-augmentation-amulet', {
  // "Add Physical Attack +1 to each human hero present. Add Light +1 to
  // each elf hero present. Add Strength +2 to each dwarf hero present." -
  // 3 independent per-trait-count pooled grants, same convention as
  // village-village-headman/village-retaliation-nodule's own "for each X
  // present" lines.
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroesOfRace = (race) => player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && def.traits.includes(race);
      });
      const humanCount = heroesOfRace('Human').length;
      const elfCount = heroesOfRace('Elf').length;
      const dwarfIds = heroesOfRace('Dwarf');
      let nextState = humanCount ? grantPhysicalAttack(state, playerId, humanCount) : state;
      nextState = elfCount ? grantLight(nextState, playerId, elfCount) : nextState;
      for (const id of dwarfIds) {
        nextState = grantTemporaryStrength(nextState, playerId, id, 2);
      }
      return nextState;
    },
  }],
});

// "Place 1 monster from the hall on the bottom of the dungeon deck and
// refill the hall, or rearrange the hall." A real 2-way choice: supplying
// choices.newOrder takes the rearrange branch (full permutation, same
// primitive as hero-bhoidwood-l3's own "Rearrange the hall"); otherwise
// defaults to the place-on-bottom branch (same hallRankIndex-fallback
// convention as Veilminder/Tuath's own hall-targeting abilities).
register('village-banish', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      let nextState = state;
      if (choices?.newOrder) {
        if (choices.newOrder.length !== state.dungeonHall.length) throw new Error('New hall order must cover every rank.');
        nextState = { ...state, dungeonHall: [...choices.newOrder] };
      } else {
        const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
        if (targetRank !== -1 && state.dungeonHall[targetRank]) {
          const monsterInstanceId = state.dungeonHall[targetRank];
          const dungeonHall = [...state.dungeonHall];
          dungeonHall[targetRank] = null;
          nextState = compactAndRefill({ ...state, dungeonHall, dungeonDeck: [...state.dungeonDeck, monsterInstanceId] }, { playerId });
        }
      }
      // "Destroy 1 card from your hand (this card can destroy itself)." No
      // getCandidates here - PlayerDock.vue's useDungeonAbility calls
      // resolveChoicesFor (the generic getCandidates picker) BEFORE its own
      // village-banish special case that shows the place/rearrange branch
      // picker; registering getCandidates here would pop the hand-card
      // picker up FIRST, ahead of that branch choice, reversing the card's
      // own read order. Fixing this needs useDungeonAbility's own
      // village-banish branch to run before resolveChoicesFor, not just an
      // engine-side registration - tracked as a known gap rather than
      // shipping the choices out of order.
      const player = nextState.players.find((p) => p.id === playerId);
      const targetId = choices?.instanceId ?? player.hand[0];
      if (targetId) nextState = destroyFromHand(nextState, playerId, targetId);
      return drawCards(nextState, playerId, 1);
    },
  }],
});

register('village-bolt-of-purity', {
  dungeon: [
    {
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
        let nextState = grantMagicAttack(state, playerId, 2);
        if (diseaseId) nextState = destroyFromHand(nextState, playerId, diseaseId);
        return nextState;
      },
    },
    // "Magic Attack +1 against monsters that give diseases." Granted now
    // (before a monster is even chosen), applied once the fought monster's
    // identity is known - combat/attackMath.js's
    // MONSTERS_THAT_GIVE_DISEASES/bonusMagicAttackAgainstDiseaseGivers.
    {
      resolve: (state, { playerId }) => updatePlayerTurnFlags(state, playerId, (tf) => ({
        ...tf, bonusMagicAttackAgainstDiseaseGivers: tf.bonusMagicAttackAgainstDiseaseGivers + 1,
      })),
    },
  ],
});

// village-cursed-mace: "Physical Attack +6" (equip-gated, automatic) -
// combat/attackMath.js parseAlwaysActiveTraits. "Gain 1 disease when
// equipped." - actions/dungeonActions.js's equipWeapon (a clean, single
// choke point for every equip, same place Dwarven Bear Hammer/Dart
// Thrower's own per-weapon special cases already live).

register('village-demon-vision', {
  // "Choose a number from 1-5. Reveal that many cards from your deck.
  // Destroy all revealed cards with a VP value less than the number you
  // chose. Add 1 remaining card to your hand. Discard the rest." Same
  // reveal-and-filter shape as Into the Abyss's own Elemental Pain Breach
  // effects (Blood/Lava/Smoke/Shadow Torment) - see revealAndFilterByVp in
  // effectRegistry/monsters.js. choices.chosenNumber defaults to 3 (the
  // midpoint of the printed 1-5 range) when not supplied.
  village: [{ resolve: (state, { playerId, choices }) => demonVisionReveal(state, playerId, choices) }],
  dungeon: [{ resolve: (state, { playerId, choices }) => demonVisionReveal(state, playerId, choices) }],
});
function demonVisionReveal(state, playerId, choices) {
  const chosenNumber = Math.min(5, Math.max(1, choices?.chosenNumber ?? 3));
  let nextState = state;
  const revealed = [];
  for (let i = 0; i < chosenNumber; i += 1) {
    const { state: afterReveal, instanceId } = revealTopOfOwnDeck(nextState, playerId);
    nextState = afterReveal;
    if (instanceId) revealed.push(instanceId);
  }
  if (!revealed.length) return nextState;
  const toDestroy = revealed.filter((id) => (getInstanceDefinition(nextState, id).points || 0) < chosenNumber);
  const remaining = revealed.filter((id) => !toDestroy.includes(id));
  for (const id of toDestroy) {
    // Revealed cards live in discard (revealTopOfOwnDeck's own convention) -
    // destroyFromHand only removes from hand, so these are stripped from
    // discard directly instead.
    nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: p.discard.filter((d) => d !== id) }));
  }
  // "Add 1 remaining card to your hand. Discard the rest." - the rest are
  // already sitting in discard (revealTopOfOwnDeck's own convention), so
  // only the 1 kept card needs to actually move.
  const [keep] = remaining;
  if (keep) {
    nextState = updatePlayer(nextState, playerId, (p) => ({
      ...p, discard: p.discard.filter((id) => id !== keep), hand: [...p.hand, keep],
    }));
  }
  return nextState;
}

register('village-doomgate-squire', {
  village: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 1) },
    { resolve: (state, { playerId }) => grantBuys(state, playerId, 1) },
  ],
  dungeon: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 1) },
    {
      // "Discard 1 XP to add Physical Attack +1 to one hero." (pooled, same
      // convention as every other "to one hero" attack grant in this file.)
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        if (player.xp < 1) return state;
        return grantPhysicalAttack(grantXp(state, playerId, -1), playerId, 1);
      },
    },
  ],
});

// "Reveal the top card of your deck. Put it back, discard it or destroy it.
// If you destroyed it, you may use this ability a second time." /
// "Destroy this card to draw 1 card." The base reveal-and-choose (put back/
// discard/destroy) IS implemented as a real 3-way choice via choices.outcome
// (Village timing resolves choices before dispatch); the "use again on a
// destroy" self-repeat is NOT implemented - the same
// once-per-card-per-turn-cap bypass shape already flagged for Skinshifter
// L3's "if disease, may use again" (Caverns of Bane). See
// docs/expansion-audit.md.
register('village-entrail-reader', {
  village: [
    {
      resolve: (state, { playerId, choices }) => {
        const { state: afterReveal, instanceId } = revealTopOfOwnDeck(state, playerId);
        if (!instanceId) return afterReveal;
        const outcome = choices?.outcome ?? 'putBack';
        if (outcome === 'putBack') {
          return updatePlayer(afterReveal, playerId, (p) => ({
            ...p, discard: p.discard.filter((id) => id !== instanceId), deck: [instanceId, ...p.deck],
          }));
        }
        if (outcome === 'destroy') {
          // The revealed card is already in discard (revealTopOfOwnDeck's own
          // convention) - "destroy" here means removing it from the game
          // entirely, so it's stripped from discard directly rather than
          // routed through destroyFromHand (which only ever operates on hand).
          return updatePlayer(afterReveal, playerId, (p) => ({
            ...p, discard: p.discard.filter((id) => id !== instanceId), stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 },
          }));
        }
        return afterReveal; // 'discard' - revealTopOfOwnDeck already left it in discard
      },
    },
    {
      resolve: (state, { playerId, instanceId }) => drawCards(destroyFromHand(state, playerId, instanceId), playerId, 1),
    },
  ],
});

register('village-fireball', {
  dungeon: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 3) }],
});

register('village-frost-giant-axe', {
  // "If no hero present has Strength 6 or higher, draw 1 card. If it is a
  // hero, add Strength +3 to that hero. (You can use this ability while
  // this card is not equipped.)"
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const hasStrongHero = player.hand.some((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && (def.strength || 0) >= 6;
      });
      if (hasStrongHero) return state;
      const before = new Set(player.hand);
      let nextState = drawCards(state, playerId, 1);
      const drawnId = nextState.players.find((p) => p.id === playerId).hand.find((id) => !before.has(id));
      if (drawnId) {
        const def = getInstanceDefinition(nextState, drawnId);
        if (isHeroLike(def)) {
          nextState = grantTemporaryStrength(nextState, playerId, drawnId, 3);
        }
      }
      return nextState;
    },
  }],
});

// village-icon-of-inversion: "Battle effects become Aftermath effects."
// Full RAW defer (Matt, confirming directly): sets
// turnFlags.battleEffectsBecomeAftermath, which combat/resolveCombat.js's
// resolveTimedHandlers checks - instead of running a Battle-timed handler
// immediately, it queues it (turnFlags.deferredBattleEffects) and the queue
// is flushed once win/loss is already decided, right alongside the fought
// monster's own Aftermath resolution. This means a Battle effect that would
// normally raise the fought monster's own Health no longer affects THIS
// fight's outcome at all (the win/loss check runs before the queue is
// flushed) - the deliberate point of "inverting" the timing, not a bug to
// route around. "When you buy this card, you may buy one additional card."
// - actions/villageActions.js's performBuy (same on-purchase-trigger
// convention as Filigree Amulet/Paired Swords).
register('village-icon-of-inversion', {
  dungeon: [{
    resolve: (state, { playerId }) => updatePlayer(state, playerId, (p) => ({
      ...p, turnFlags: { ...p.turnFlags, battleEffectsBecomeAftermath: true },
    })),
  }],
});

// village-lightstone-gem: printed Light stat only, no ability text.

// village-polearm: "Physical Attack +2" (equip-gated, automatic) -
// combat/attackMath.js parseAlwaysActiveTraits. "Add Physical Attack +4
// while equipped to a hero with Strength 8 or higher." -
// combat/attackMath.js contributesTraits (equipped-hero Strength lookup,
// same shape as Snakehead Flail's own equipped-hero level lookup).

// Heroes in discard that could ACTUALLY level up right now - enough XP, not
// already leveled this turn, a next level to go to. performLevelUp is a pure
// reducer, so a dry run is the one check that can't drift from the real rules.
function reclusiveMysticCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.discard.filter((id) => {
    if (!isHeroLike(getInstanceDefinition(state, id))) return false;
    try {
      performLevelUp(state, playerId, id, { payXp: true, destination: 'discard' });
      return true;
    } catch {
      return false;
    }
  });
}

register('village-reclusive-mystic', {
  village: [
    {
      // "If you have 0 or 1 XP, gain 1 XP."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        return player.xp <= 1 ? grantXp(state, playerId, 1) : state;
      },
    },
    {
      // "Destroy this card to level up a hero in your discard pile (pay all
      // costs)." The only card in the pool needing a level-up sourced from
      // DISCARD rather than hand - performLevelUp's own hand-removal filter
      // is a harmless no-op for a card that was never in hand, so this just
      // additionally strips it from discard first.
      //
      // Bug report -P2GUHQ3KsZBh0TKnfbv (Matt): "should allow me to choose
      // which hero in my discard pile... It seems to try to just
      // automatically pick and that's not working." It had no picker and
      // took the first hero in discard whether or not it could level (his
      // was Harruli Spellsword: 3 XP needed, 2 held). Now the picker offers
      // only heroes that can level, and says why when none can.
      resolve: (state, { playerId, instanceId, choices }) => {
        const candidates = reclusiveMysticCandidates(state, { playerId });
        if (!candidates.length) {
          throw new Error('No hero in your discard pile can level up right now (not enough XP, or already leveled this turn).');
        }
        const targetId = choices?.heroInstanceId ?? candidates[0];
        if (!candidates.includes(targetId)) throw new Error('That hero cannot level up right now.');
        let nextState = updatePlayer(state, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== targetId) }));
        nextState = destroyFromHand(nextState, playerId, instanceId);
        return performLevelUp(nextState, playerId, targetId, { payXp: true, destination: 'discard' });
      },
      getCandidates: reclusiveMysticCandidates,
    },
  ],
});

register('village-sage', {
  // "Destroy 1 card (this card can destroy itself). If the card you destroy
  // is not a hero, gain 1 XP." / "Destroy the card. Gain 1 gold for each
  // hero present." Two independent Village-ability lines.
  //
  // Matt reported this directly (first pass): "I just used the sage. The
  // village ability allows me to destroy a card and it just destroyed a
  // card for me. It didn't let me choose which one." Ability #1 needed a
  // real getCandidates picker over the whole hand - PlayerDock.vue's
  // resolveChoicesFor only shows a picker for abilities that register one
  // (see effectRegistry/index.js's own comment); without it, resolve's own
  // fallback was silently the ONLY behavior anyone ever saw.
  //
  // Matt's follow-up correction, after that first fix shipped: "[Ability 2]
  // isn't actually targeting itself. It seems to just be targeting like the
  // first card in my hand... for the second ability, it only should be able
  // to target itself I think." The first fix wrongly gave ability #2 the
  // same "any card in hand" picker as ability #1, reading "Destroy the
  // card" as a second independent free choice - Matt's correction is that
  // "the card" here means Sage itself (no antecedent "1 card" of its own,
  // unlike ability #1's explicit "1 card"), so ability #2 is a plain,
  // unconditional self-destroy - no choice, no picker, same shape as every
  // other "Destroy this card to gain X" line elsewhere in this file (e.g.
  // village-hysterical-villager's second ability).
  village: [
    {
      resolve: (state, { playerId, instanceId, choices }) => {
        const player = state.players.find((p) => p.id === playerId);
        const targetId = choices?.instanceId ?? player.hand.find((id) => id !== instanceId) ?? instanceId;
        if (!targetId) return state;
        const wasHero = isHeroLike(getInstanceDefinition(state, targetId));
        let nextState = destroyFromHand(state, playerId, targetId);
        if (!wasHero) nextState = grantXp(nextState, playerId, 1);
        return nextState;
      },
      getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand,
    },
    {
      resolve: (state, { playerId, instanceId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const heroCount = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id))).length;
        let nextState = destroyFromHand(state, playerId, instanceId);
        if (heroCount) nextState = grantGold(nextState, playerId, heroCount);
        return nextState;
      },
    },
  ],
});

// village-short-bow: "Physical Attack +3 against rank 2 or higher." / "Add
// Physical Attack +2 while equipped to a ranger." -
// combat/attackMath.js contributesTraits (both the same shape as Dart
// Thrower's own rank-conditional and Ranger-equipped bonuses).

register('village-swamp-provisions', {
  dungeon: [
    {
      // "Add Strength +3 to 1 hero."
      resolve: (state, { playerId, choices }) => {
        const player = state.players.find((p) => p.id === playerId);
        const candidates = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
        const targetId = choices?.heroInstanceId ?? candidates[0];
        if (!targetId) return state;
        return grantTemporaryStrength(state, playerId, targetId, 3);
      },
    },
    {
      // "Destroy this card. Add Physical Attack +3 to 1 hero. Each player
      // gains 1 disease."
      resolve: (state, { playerId, instanceId }) => {
        let nextState = grantPhysicalAttack(destroyFromHand(state, playerId, instanceId), playerId, 3);
        for (const p of state.players) nextState = gainCurse(nextState, p.id);
        return nextState;
      },
    },
  ],
});

register('village-toryn-gauntlet', {
  // "Add Magic Attack +2 and Strength +2 to one hero. You may consider that
  // hero to be equipped with a magic weapon." The Attack/Strength grant is
  // implemented; "consider equipped with a magic weapon" is a fictional
  // status with no other card interaction currently keyed off it (same
  // simplification already accepted for Blademage's "still a spell, also a
  // weapon" - see docs/expansion-audit.md item 27).
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidates = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
      const targetId = choices?.heroInstanceId ?? candidates[0];
      if (!targetId) return state;
      const nextState = grantTemporaryStrength(state, playerId, targetId, 2);
      return grantMagicAttack(nextState, playerId, 2);
    },
  }],
});

// ==========================================================================
// Worlds Collide
// ==========================================================================
// No rulebook available (same caveat as Numenera/Start/ItA) - extra caution
// on anything ambiguous. Several "no explicit player choice offered" spots
// below auto-pick a reasonable default (cheapest/lowest-value/first-found)
// rather than building a one-off choice UI for a single card - documented
// inline, same convention as the Monsters/Heroes passes just before this.

// Chieftain's Drum: "Choose hero, village, or monster. Reveal the top card
// of your deck. If you named the card's type correctly, draw 2 cards. If
// not, return it." Same text for both Village and Dungeon timing, so one
// shared resolve function backs both registrations below.
//
// Matt (bug report): "In either case I should get to choose what type
// before it draws and it should show me what got drawn. Probably in a
// modal." guessedType still defaults to 'hero' when a caller doesn't
// supply one (the AI bot has no dedicated Chieftain's Drum logic and never
// will supply a real guess - same "auto-pick a reasonable default" allowance
// as this whole Worlds Collide pass documents above) - PlayerDock.vue's own
// human-facing flow now always prompts for a real one before dispatching,
// which is the actual fix; this function only needed to also report what
// happened. meta.lastChieftainsDrumReveal is a transient field, not part of
// the rules (same convention as meta.lastCombatResult - see
// dungeonActions.js's own comment) - PlayerDock.vue reads it right after
// dispatch to show the revealed card and the guess/outcome in a modal.
function chieftainsDrumResolve(state, { playerId, choices }) {
  const guessedType = choices?.guessedType ?? 'hero';
  const { state: afterLook, instanceIds: revealed } = lookAtTopOfDeck(state, playerId, 1);
  const [revealedId] = revealed;
  if (!revealedId) return afterLook;
  const def = getInstanceDefinition(afterLook, revealedId);
  const matched = def.type === guessedType;
  const reveal = {
    ...afterLook.meta, lastChieftainsDrumReveal: { instanceId: revealedId, guessedType, matched },
  };
  if (matched) {
    // "Draw 2 cards" - the revealed card itself was already correctly
    // guessed and is committed to discard (the rulebook's normal "Reveal"
    // default), then 2 fresh cards are drawn off the remaining deck.
    const discarded = updatePlayer(
      { ...afterLook, meta: reveal },
      playerId,
      (p) => ({ ...p, discard: [...p.discard, revealedId] }),
    );
    return drawCards(discarded, playerId, 2);
  }
  // "If not, return it" - back on top of the deck, undoing the look.
  return updatePlayer(
    { ...afterLook, meta: reveal },
    playerId,
    (p) => ({ ...p, deck: [revealedId, ...p.deck] }),
  );
}

// Ritual of Cleansing: "Discard the top 5 cards of any player's deck. That
// player destroys 2 of those cards. If this targeted a player other than
// you, destroy this card." Same text for both Village and Dungeon timing.
// "That player destroys 2" has no explicit choice UI here - auto-picks the
// 2 lowest goldValue cards among the 5 discarded (documented simplification,
// same convention as other auto-picked WC effects above).
function ritualOfCleansingResolve(state, { playerId, instanceId, choices }) {
  const targetId = choices?.targetPlayerId ?? playerId;
  const target = state.players.find((p) => p.id === targetId);
  if (!target) return state;
  const deck = [...target.deck];
  const revealed = deck.splice(0, 5);
  if (!revealed.length) return state;
  const sorted = [...revealed].sort(
    (a, b) => goldValueOf(getInstanceDefinition(state, a)) - goldValueOf(getInstanceDefinition(state, b)),
  );
  const toDestroy = new Set(sorted.slice(0, 2));
  const toDiscard = revealed.filter((id) => !toDestroy.has(id));
  let nextState = updatePlayer({ ...state, players: state.players.map((p) => (p.id === targetId ? { ...p, deck } : p)) },
    targetId, (p) => ({ ...p, discard: [...p.discard, ...toDiscard], stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + toDestroy.size } }));
  if (targetId !== playerId && instanceId) {
    const caster = nextState.players.find((p) => p.id === playerId);
    if (caster.hand.includes(instanceId)) nextState = destroyFromHand(nextState, playerId, instanceId);
  }
  return nextState;
}

// Time Bend: "Draw 1 card. Set aside a card that you have not used. Add
// that card to your hand after you refill your hand at the end of the
// turn." Shares turnFlags.keptForNextHandInstanceIds with Stoneguard's own
// Spoils (actions/turnActions.js's endTurn). "A card you have not used" -
// auto-picks the first hand card not yet in usedAbilityInstanceIds this
// turn (excluding this card itself, already mid-use) rather than a one-off
// choice UI. Same text for both Village and Dungeon timing.
function timeBendResolve(state, { playerId, instanceId, choices }) {
  let nextState = drawCards(state, playerId, 1);
  const player = nextState.players.find((p) => p.id === playerId);
  const usedIds = new Set(player.turnFlags.usedAbilityInstanceIds.map((entry) => entry.split(':')[0]));
  const candidateId = choices?.instanceId
    ?? player.hand.find((id) => id !== instanceId && !usedIds.has(id));
  if (!candidateId) return nextState;
  return updatePlayer(nextState, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, keptForNextHandInstanceIds: [...p.turnFlags.keptForNextHandInstanceIds, candidateId] },
  }));
}

register('village-ambrosia', {
  // "Add Physical Attack +1 and Strength +2 to each hero." Both halves
  // are per-hero (found and fixed alongside the identical bug in
  // village-dwarven-ale, which shares this exact wording and used to
  // share this exact mistake: Physical Attack was granted once, flat,
  // outside the loop, instead of once per hero into the pooled total).
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      for (const id of player.hand) {
        const def = getInstanceDefinition(nextState, id);
        if (!isHeroLike(def)) continue;
        nextState = grantTemporaryStrength(nextState, playerId, id, 2);
        nextState = grantPhysicalAttack(nextState, playerId, 1);
      }
      // "Whenever a Battle or Aftermath effect destroys a hero, discard
      // that hero instead." actions/handChoiceActions.js's
      // heroDestroyRedirectsToDiscard.
      return updatePlayer(nextState, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, battleAftermathDestroyRedirectsToDiscard: true },
      }));
    },
  }],
});

register('village-blessed-hammer', {
  // "Place the monster in rank 1 on the bottom of the dungeon deck. Refill
  // the hall." Same place-on-bottom-and-refill shape as village-banish's
  // own branch, fixed to rank 1 (index 0) rather than a player-chosen rank.
  dungeon: [{
    resolve: (state, { playerId }) => {
      const monsterInstanceId = state.dungeonHall[0];
      if (!monsterInstanceId) return state;
      const dungeonHall = [...state.dungeonHall];
      dungeonHall[0] = null;
      return compactAndRefill({ ...state, dungeonHall, dungeonDeck: [...state.dungeonDeck, monsterInstanceId] }, { playerId });
    },
  }],
});

// Brian: "The border guards draw a card / discard a card discards a card at
// random instead of letting me choose what to discard."
//
// It wasn't random - both handlers fell back to `player.hand[0]`, the
// leftmost card, whenever no `choices.instanceId` was threaded in, and
// nothing ever queued a choice for the player to answer, so that fallback
// WAS the behavior every time. Now a normal queueHandChoice, the same way
// every other "Discard 1 card" line works.
//
// The two halves were also in the wrong order: the printed text is "Draw 1
// card. Discard 1 card.", so the drawn card has to be discardable - it's
// the whole point of the ability (dig one deeper, throw away the worst
// card you now hold). Drawing first and queueing the choice off the
// POST-draw hand fixes that, and matches hero-learned-l3's own
// "Draw 2 cards. Discard 1 card." handler exactly.
const drawOneDiscardOne = (reason) => (state, { playerId }) => {
  const nextState = drawCards(state, playerId, 1);
  const player = nextState.players.find((p) => p.id === playerId);
  return queueHandChoice(nextState, {
    playerId, verb: 'discard', candidateInstanceIds: player.hand, reason,
  });
};

register('village-border-guard', {
  village: [{ resolve: drawOneDiscardOne('Border Guard: discard 1 card.') }],
  dungeon: [
    { resolve: drawOneDiscardOne('Border Guard: discard 1 card.') },
    { resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) },
  ],
  // "React: Destroy this card to prevent 1 hero from being destroyed by a
  // Battle or Aftermath effect." - effectHelpers.js's
  // findEligibleShieldBearer/queueShieldBearerOffer, generalized to also
  // recognize this card alongside village-shield-bearer. No register()
  // entry needed for the react itself.
});

register('village-chalice-mace', {
  // "specialEffects: Clerics require 3 less Strength to equip." -
  // actions/dungeonActions.js's equipWeapon (effectiveWeight). "Add Light
  // +1 while equipped to a cleric." - combat/lightDarkness.js's
  // computeTotalLight.
});

register('village-chieftains-drum', {
  village: [{ resolve: chieftainsDrumResolve }],
  dungeon: [{ resolve: chieftainsDrumResolve }],
});

register('village-creeping-death', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      let nextState = state;
      // "Each other player who has not gained a disease this turn gains 1
      // disease." turnFlags.gainedDiseaseThisTurn is set unconditionally
      // by gainCurse itself (effectHelpers.js) - checked BEFORE this card's
      // own grants so a player who already gained one earlier this same
      // turn is correctly skipped.
      for (const p of state.players) {
        if (p.id === playerId) continue;
        const current = nextState.players.find((pp) => pp.id === p.id);
        if (!current.turnFlags.gainedDiseaseThisTurn) nextState = gainCurse(nextState, p.id);
      }
      // "Each monster in the hall suffers Health -2. If this reduces a
      // monster's Health to 0, place that monster into your discard pile
      // and refill the hall. (This does not count as defeating a monster.
      // Do not collect XP.)"
      let dungeonHall = [...nextState.dungeonHall];
      let discardIds = [];
      dungeonHall = dungeonHall.map((id) => {
        if (!id) return id;
        const def = getInstanceDefinition(nextState, id);
        const newHealth = Math.max(0, (def.health || 0) - 2);
        nextState = applyInstanceOverride(nextState, id, { health: newHealth });
        if (newHealth > 0) return id;
        discardIds.push(id);
        return null;
      });
      if (discardIds.length) {
        nextState = updatePlayer({ ...nextState, dungeonHall }, playerId, (p) => ({ ...p, discard: [...p.discard, ...discardIds] }));
        nextState = compactAndRefill(nextState, { playerId });
      }
      return nextState;
    },
  }],
});

register('village-dredging-net', {
  // "Draw 3 cards from the bottom of your deck. Discard 1, destroy 1, and
  // place 1 in your hand." No "of your choice" text for the 3-way split -
  // auto-assigns in draw order (1st kept in hand, 2nd discarded, 3rd
  // destroyed) rather than a one-off 3-way choice UI for this single card.
  village: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const deck = [...player.deck];
      const drawn = deck.splice(Math.max(0, deck.length - 3), 3).reverse();
      if (!drawn.length) return state;
      const [toHand, toDiscard, toDestroy] = drawn;
      let nextState = updatePlayer(state, playerId, (p) => ({
        ...p, deck, hand: toHand ? [...p.hand, toHand] : p.hand, discard: toDiscard ? [...p.discard, toDiscard] : p.discard,
      }));
      if (toDestroy) {
        nextState = updatePlayer(nextState, playerId, (p) => ({
          ...p, stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 },
        }));
      }
      return nextState;
    },
  }],
});

register('village-drill-sergeant', {
  village: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const count = Math.min(5, Math.floor(player.xp / 2));
      return count ? drawCards(state, playerId, count) : state;
    },
  }],
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidates = player.hand.filter((id) => getInstanceDefinition(state, id).id === 'basic-regular');
      const targetId = choices?.instanceId ?? candidates[0];
      if (!targetId) throw new Error('No Regular present to destroy.');
      return grantXp(drawCards(destroyFromHand(state, playerId, targetId), playerId, 2), playerId, 1);
    },
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).id === 'basic-regular'),
  }],
});

register('village-feast', {
  // "Add Physical Attack +1 and Strength +3 to each hero." Both halves
  // are per-hero (found and fixed alongside the same bug in
  // village-dwarven-ale and village-ambrosia - all three shared the same
  // mistake: Physical Attack granted once, flat, outside the loop,
  // instead of once per hero into the pooled total).
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      for (const id of player.hand) {
        const def = getInstanceDefinition(nextState, id);
        if (!isHeroLike(def)) continue;
        nextState = grantTemporaryStrength(nextState, playerId, id, 3);
        nextState = grantPhysicalAttack(nextState, playerId, 1);
      }
      return nextState;
    },
  }],
});

// village-flaming-sword: printed alwaysActive Magic Attack +3 (and Light
// +1) only, no Village/Dungeon ability text - handled generically. Title was
// a source-data typo ("Dword" instead of "Sword") - confirmed against the
// card scan and fixed at the source (WC numeric-stat audit, 2026-07-22; see
// docs/expansion-audit.md). The definition id changes from
// village-flaming-dword to village-flaming-sword as a result - nothing else
// in the engine keyed off the old id (checked).

register('village-guardian-blade', {
  // "specialEffects: If the equipped hero would be destroyed, discard that
  // hero instead." - actions/handChoiceActions.js's
  // heroDestroyRedirectsToDiscard (checks for this weapon specifically
  // among the target hero's own equipped weapons).
});

register('village-guide', {
  // "You may buy 1 additional card." Same generic bonusBuys grant as
  // Filigree Amulet/Icon of Inversion.
  village: [{ resolve: (state, { playerId }) => updatePlayer(state, playerId, (p) => ({ ...p, turnFlags: { ...p.turnFlags, bonusBuys: p.turnFlags.bonusBuys + 1 } })) }],
});

register('village-jondul-bow', {
  // "specialEffects: Physical Attack +4 against rank 2 or higher" - handled
  // in combat/attackMath.js's contributesTraits (JONDUL_BOW_RANK_BONUS).
  dungeon: [{
    // "Switch the positions of 2 adjacent monsters in the hall."
    resolve: (state, { choices }) => {
      const rankA = choices?.hallRankIndex ?? state.dungeonHall.findIndex((id, i) => id && state.dungeonHall[i + 1]);
      if (rankA === -1 || rankA === undefined || rankA >= state.dungeonHall.length - 1) return state;
      return moveHallCard(state, rankA, rankA + 1);
    },
  }],
});

register('village-ritual-of-cleansing', {
  village: [{ resolve: ritualOfCleansingResolve }],
  dungeon: [{ resolve: ritualOfCleansingResolve }],
});

register('village-soulfire', {
  dungeon: [
    // "Magic Attack +1" - printed under `dungeons`, not `attacks`, so it's
    // an ACTIVATED grant (needs USE_DUNGEON_ABILITY), not a passive trait.
    { resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) },
    {
      // "Discard up to 5 XP. Add Magic Attack +1 for each XP discarded."
      resolve: (state, { playerId, choices }) => {
        const player = state.players.find((p) => p.id === playerId);
        const amount = Math.min(5, choices?.amount ?? player.xp);
        if (!amount) return state;
        return grantMagicAttack(grantXp(state, playerId, -amount), playerId, amount);
      },
    },
  ],
});

register('village-spear', {
  dungeon: [{
    // "Add Physical Attack +3 while this weapon is equipped." (in addition
    // to its own printed base "Physical Attack +2," already generically
    // applied by attackMath.js's alwaysActive parsing while equipped.)
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      return isWeaponEquipped(player, instanceId) ? grantPhysicalAttack(state, playerId, 3) : state;
    },
  }],
  aftermath: [{
    // "Destroy this card if you used its Dungeon ability."
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const usedThisTurn = player.turnFlags.usedAbilityInstanceIds.includes(`${instanceId}:dungeon:0`);
      return usedThisTurn && player.hand.includes(instanceId) ? destroyFromHand(state, playerId, instanceId) : state;
    },
  }],
});

register('village-time-bend', {
  // "Draw 1 card. Set aside a card that you have not used. Add that card
  // to your hand after you refill your hand at the end of the turn." Shares
  // turnFlags.keptForNextHandInstanceIds with Stoneguard's own Spoils
  // (actions/turnActions.js's endTurn). "A card you have not used" - auto-
  // picks the first hand card not yet in usedAbilityInstanceIds this turn
  // (excluding this card itself, already mid-use) rather than a one-off
  // choice UI.
  village: [{ resolve: timeBendResolve }],
  dungeon: [{ resolve: timeBendResolve }],
});

register('village-town-guard', {
  // 2 independent Village abilities (no "instead" text linking them).
  village: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 2) },
    {
      resolve: (state, { playerId, instanceId }) => {
        const player = state.players.find((p) => p.id === playerId);
        if (!player.hand.includes(instanceId)) return state;
        return drawCards(destroyFromHand(state, playerId, instanceId), playerId, 3);
      },
    },
  ],
});

// Brian reported this directly: "When I used the Trader's village ability
// to put a card of up to 2 gold cost more in my discard pile, it chose
// the card for me instead of letting me choose after I selected which
// card I would destroy." "Costs" here is the destroyed card's own
// purchase price (def.cost), not its goldValue (the gold it provides
// toward a purchase while in hand) - a distinct field, see
// effectHelpers.js's goldValueOf. Unlike Gray Ooze/Isri/Clan and the
// other findCheapestBuyableSource callers (whose printed text has no
// "of your choice" wording and where auto-picking the cheapest is an
// accepted simplification - see that function's own comment), Trader got
// a real reported complaint about it, so this is the one exception:
// choices.source (a {type, key} pair the client already resolved via a
// PlayerDock.vue picker, once destroyedCost is known - see
// useVillageAbility's own village-trader branch) is used if present,
// falling back to the old auto-cheapest behavior only as a defensive
// no-op-safe default if a client ever dispatches this directly without
// going through that picker.
register('village-trader', {
  village: [{
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = choices?.instanceId ?? player.hand[0];
      if (!targetId) return state;
      const destroyedCost = getInstanceDefinition(state, targetId).cost || 0;
      const afterDestroy = destroyFromHand(state, playerId, targetId);
      const source = choices?.source
        ?? findCheapestBuyableSource(afterDestroy, (def) => (def.cost || 0) <= destroyedCost + 2);
      return source ? performBuy(afterDestroy, playerId, source, { requireGoldCheck: false }) : afterDestroy;
    },
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand,
  }],
  dungeon: [{
    // "Draw 1 card. If it is a monster, draw another card."
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const beforeHand = new Set(player.hand);
      let nextState = drawCards(state, playerId, 1);
      const afterHand = nextState.players.find((p) => p.id === playerId).hand;
      const drawnId = afterHand.find((id) => !beforeHand.has(id));
      if (drawnId && getInstanceDefinition(nextState, drawnId).type === 'monster') {
        nextState = drawCards(nextState, playerId, 1);
      }
      return nextState;
    },
  }],
});
